<script setup lang="ts">
import { computed } from 'vue'
import { useAllianceStore } from '@/stores/allianceStore'
import { useGameStore } from '@/stores/gameStore'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Stores
const allianceStore = useAllianceStore()
const gameStore = useGameStore()

// Check if current player is alliance leader
const isAllianceLeader = computed(() => {
  if (!gameStore.player?.allianceId) {
    return false
  }
  const alliance = allianceStore.alliances.get(gameStore.player.allianceId)
  return alliance?.leaderPlayerId === gameStore.player?.id
})

// Get the player's alliance
const playerAlliance = computed(() => {
  if (!gameStore.player?.allianceId) {
    return null
  }
  return allianceStore.alliances.get(gameStore.player.allianceId)
})

// Get the count of join requests
const joinRequestsCount = computed(() => {
  if (!playerAlliance.value || !playerAlliance.value.joinRequests) {
    return 0
  }
  return playerAlliance.value.joinRequests.length
})

// Define tabs with router configuration
const tabs = computed(() => {
  const baseTabs = [
    {
      name: t('diplomacy.alliance.tabs.members'),
      target: '/diplomacy/alliance/members',
      activeKey: 'AllianceMembers'
    },
    {
      name: t('diplomacy.alliance.tabs.research'),
      target: '/diplomacy/alliance/research',
      activeKey: 'AllianceResearch'
    },
    {
      name: t('diplomacy.alliance.tabs.stats'),
      target: '/diplomacy/alliance/stats',
      activeKey: 'AllianceStats'
    }
  ]

  // Add Join Requests tab only for alliance leader
  if (isAllianceLeader.value) {
    baseTabs.push({
      name: t('diplomacy.alliance.tabs.joinRequests'),
      target: '/diplomacy/alliance/requests',
      activeKey: 'AllianceRequests'
    })
  }

  return baseTabs
})
</script>

<template>
  <div class="text-sm font-medium text-center border-b text-metal-400 border-metal-700">
    <div class="overflow-x-auto whitespace-nowrap no-scrollbar">
      <ul class="flex">
        <li v-for="tab in tabs" :key="tab.name" class="me-2">
          <router-link
            :to="tab.target"
            :class="{
              'active-blue text-blue-500 border-blue-500': $route.name === tab.activeKey,
              'text-metal-500 border-transparent': $route.name !== tab.activeKey,
              'inline-block p-4 border-b-2 rounded-t-lg hover:border-metal-300 hover:text-metal-300': true
            }"
          >
            {{ tab.name }}
            <span 
              v-if="tab.activeKey === 'AllianceRequests' && joinRequestsCount > 0" 
              class="bg-red-500 rounded ms-1 px-1 text-xs text-white"
            >
              {{ joinRequestsCount }}
            </span>
          </router-link>
        </li>
      </ul>
    </div>
  </div>
  
  <!-- Router view to render the correct component based on the route -->
  <router-view class="mt-2"></router-view>
</template>

<style scoped lang="scss">
.nav-item {
  cursor: pointer;
}

.active-blue {
  background: linear-gradient(
    to top,
    rgba(59, 130, 246, 0.3) 0%,
    rgba(59, 130, 246, 0) 70%
  );
}
</style>
